import type { ILookupOption } from '../../types/ILookup';

export const ACCOUNT_COLUMNS = [
  { field: 'Name', label: 'Name' },
  { field: 'Site', label: 'Site' },
] as const;

export const CONTACT_COLUMNS = [
  { field: 'Name', label: 'Name' },
  { field: 'Email', label: 'Email' },
  { field: 'Phone', label: 'Phone Number' },
] as const;

export const SHIP_TO_COLUMNS = [
  { field: 'Name', label: 'Address Name' },
  { field: 'O_Addressee__c', label: 'Addressee' },
  { field: 'AddressLine1__c', label: 'Address line1' },
  { field: 'City__c', label: 'City' },
  { field: 'State_Province__c', label: 'State/Province' },
  { field: 'Country__c', label: 'Country' },
  { field: 'O_Warehouse_Ship_To__c', label: 'Warehouse Ship To' },
  { field: 'SPLC__c', label: 'SPLC' },
] as const;

export const PAYMENT_TERM_COLUMNS = [
  { field: 'Name', label: 'Name' },
  { field: 'O_Payment_Term_Description__c', label: 'Description' },
] as const;

export type AccountField = (typeof ACCOUNT_COLUMNS)[number]['field'];
export type ContactField = (typeof CONTACT_COLUMNS)[number]['field'];
export type ShipToField = (typeof SHIP_TO_COLUMNS)[number]['field'];
export type PaymentTermField = (typeof PAYMENT_TERM_COLUMNS)[number]['field'];
export type AccountFilters = Partial<Record<AccountField, string>>;
export type ContactFilters = Partial<Record<ContactField, string>>;
export type ShipToFilters = Partial<Record<ShipToField, string>>;
export type PaymentTermFilters = Partial<Record<PaymentTermField, string>>;

/** A GraphQL UI API field wrapper, e.g. `{ value: "Acme" }`. */
interface ISfGraphQlFieldValue {
  value: null | string;
}

export interface ISfShipTo {
  AddressLine1__c?: ISfGraphQlFieldValue;
  City__c?: ISfGraphQlFieldValue;
  Country__c?: ISfGraphQlFieldValue;
  Id: string;
  Name?: ISfGraphQlFieldValue;
  O_Addressee__c?: ISfGraphQlFieldValue;
  O_Warehouse_Ship_To__c?: ISfGraphQlFieldValue;
  SPLC__c?: ISfGraphQlFieldValue;
  State_Province__c?: ISfGraphQlFieldValue;
  Type__c?: ISfGraphQlFieldValue;
}

/** Raw Account fields the host's `ACCOUNT_LOOKUP_QUERY` selects, unmapped. */
export interface ISfAccount {
  Id: string;
  Name?: ISfGraphQlFieldValue;
  Site?: ISfGraphQlFieldValue;
}

/** Raw Contact fields the host's `CONTACT_LOOKUP_QUERY` selects, unmapped. */
export interface ISfContact {
  Email?: ISfGraphQlFieldValue;
  Id: string;
  Name?: ISfGraphQlFieldValue;
  Phone?: ISfGraphQlFieldValue;
}

/** Raw Payment Term fields the host's `PAYMENT_TERM_LOOKUP_QUERY` selects, unmapped. */
export interface ISfPaymentTerm {
  Id: string;
  Name?: ISfGraphQlFieldValue;
  O_Payment_Term_Description__c?: ISfGraphQlFieldValue;
}

const fieldValue = (field: ISfGraphQlFieldValue | undefined): string | undefined => field?.value ?? undefined;

const joinNonBlank = (parts: Array<string | undefined>): string | undefined =>
  parts.filter((part): part is string => Boolean(part?.trim())).join(' • ') || undefined;

export const toAccountOption = (record: ISfAccount): ILookupOption => ({
  id: record.Id,
  label: fieldValue(record.Name) ?? 'Unnamed Account',
});

export const toContactOption = (record: ISfContact): ILookupOption => ({
  id: record.Id,
  label: fieldValue(record.Name) ?? 'Unnamed Contact',
  subTitle: joinNonBlank([fieldValue(record.Email), fieldValue(record.Phone)]),
});

export const toShipToOption = (record: ISfShipTo): ILookupOption => ({
  id: record.Id,
  label: fieldValue(record.Name) ?? 'Unnamed Ship To',
});

export const toPaymentTermOption = (record: ISfPaymentTerm): ILookupOption => ({
  id: record.Id,
  label: fieldValue(record.Name) ?? 'Unnamed Payment Term',
  subTitle: fieldValue(record.O_Payment_Term_Description__c),
});

export const shipToCellValue = (record: ISfShipTo, field: ShipToField): string => fieldValue(record[field]) ?? '';
export const accountCellValue = (record: ISfAccount, field: AccountField): string => fieldValue(record[field]) ?? '';
export const contactCellValue = (record: ISfContact, field: ContactField): string => fieldValue(record[field]) ?? '';
export const paymentTermCellValue = (record: ISfPaymentTerm, field: PaymentTermField): string =>
  fieldValue(record[field]) ?? '';
